import { z } from 'zod';

import { generateZodFallback } from '@udacity/zod-fallback';

import { JWT_COOKIE } from '~/features/cookies/constants';
import { removeCookie } from '~/features/cookies/utils/remove-cookie';
import { publicEnv } from '~/features/environment/public';
import { logger } from '~/features/logging/logger';
import { MiddlewareError } from '~/features/middleware/errors';

const graphqlErrorDataSchema = z.object({
  errors: z.array(z.object({ message: z.string() })),
});

export type GraphqlErrorData = z.infer<typeof graphqlErrorDataSchema>;

export class QueryUdacityServiceError extends Error {
  public dataReceived: any;
  public statusCode: number;

  constructor(
    message: string,
    { dataReceived, statusCode, ...options }: ErrorOptions & { dataReceived: any; statusCode: number }
  ) {
    super(message, options);
    this.name = 'QueryUdacityServiceError';
    this.dataReceived = dataReceived;
    this.statusCode = statusCode;
    Object.setPrototypeOf(this, QueryUdacityServiceError.prototype);
  }
}

export class QueryUdacityServiceGraphqlError extends Error {
  public dataReceived: any;
  public statusCode: number;

  constructor(
    message: string,
    { dataReceived, statusCode, ...options }: ErrorOptions & { dataReceived: any; statusCode: number }
  ) {
    super(message, options);
    this.name = 'QueryUdacityServiceGraphqlError';
    this.dataReceived = dataReceived;
    this.statusCode = statusCode;
    Object.setPrototypeOf(this, QueryUdacityServiceGraphqlError.prototype);
  }
}

export class QueryUdacitySchemaParsingError extends Error {
  public dataReceived: any;
  public statusCode: number;

  constructor(
    message: string,
    { dataReceived, statusCode, ...options }: ErrorOptions & { dataReceived: any; statusCode: number }
  ) {
    super(message, options);
    this.name = 'QueryUdacitySchemaParsingError';
    this.dataReceived = dataReceived;
    this.statusCode = statusCode;
    Object.setPrototypeOf(this, QueryUdacitySchemaParsingError.prototype);
  }
}

export async function queryUdacityService<T extends z.ZodTypeAny>({
  url,
  method = 'POST',
  zodSchema,
  authorization,
  body,
  preferredLanguage,
  forcedLanguage,
  shouldThrowOnGraphqlError,
  testCookieHeader,
}: {
  url: string;
  method?: 'GET' | 'POST' | 'DELETE' | 'PATCH';
  zodSchema: T | null;
  authorization?: string;
  body?: string;
  preferredLanguage?: string;
  forcedLanguage?: string;
  shouldThrowOnGraphqlError?: boolean;
  testCookieHeader?: string;
}) {
  const response = await fetch(url, {
    method: method,
    headers: {
      'Content-Type': 'application/json',
      ...(authorization && { authorization }),
      ...(preferredLanguage && { 'Udacity-Preferred-Language': preferredLanguage }),
      ...(forcedLanguage && { 'Udacity-Forced-Language': forcedLanguage }),
      // forward test cookies from server-side requests when testing server-rendered pages
      ...(testCookieHeader && publicEnv.NEXT_PUBLIC_ENVIRONMENT_NAME === 'test' && { cookie: testCookieHeader }),
    },
    body: body,
    // Note: we want to make sure `testState` cookies are being included with with requests in Playwright tests.
    ...(publicEnv.NEXT_PUBLIC_ENVIRONMENT_NAME === 'test' && { credentials: 'include' }),
  }).catch((error) => {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }

    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      logger.fatal('CORS error fetching from Udacity service', {
        file: 'src/utils/query-udacity-service.ts',
        function: 'queryUdacityService',
        url,
        method,
        body,
        error,
      });
    } else {
      logger.fatal('Error fetching from Udacity service', {
        file: 'src/utils/query-udacity-service.ts',
        function: 'queryUdacityService',
        url,
        method,
        body,
        error,
      });
    }

    throw error;
  });

  if (!response.ok) {
    let dataReceived = await response.json().catch(() => {});

    logger.fatal('Error querying udacity service.', {
      file: 'src/utils/query-udacity-service.ts',
      function: 'queryUdacityService',
      statusCode: response.status,
      dataReceived: JSON.stringify(dataReceived),
      url: url,
      body: body,
    });

    // If we get a 401 here, and the user has a JWT - it *likely* means that the JWT is invalid.
    if (response.status === 401) {
      if (typeof window !== 'undefined') {
        removeCookie(JWT_COOKIE);
        window.location.replace('/');
      } else {
        throw new MiddlewareError({ name: 'INVALID_JWT', message: 'Invalid JWT' });
      }
    }

    throw new QueryUdacityServiceError('Error querying udacity service from client.', {
      dataReceived: dataReceived,
      statusCode: response.status,
      cause: {
        statusCode: response.status,
      },
    });
  }

  let data: unknown | null = null;

  // If everything is good at this point we can get the data from the response, assuming it's not a 204 response.
  // There exists cases where Udacity API data returns no data with a 200 status code unfortunately.
  if (zodSchema && response.status !== 204) {
    try {
      data = await response.json();
    } catch (error) {
      logger.fatal('Invalid JSON response from Udacity service.', {
        file: 'src/utils/query-udacity-service.ts',
        function: 'queryUdacityService',
        url,
        method,
        body,
        error,
      });

      throw error;
    }
  }

  // If graphql service responds with 200, but with errors, throw an error so that the zod fallback isn't used.
  if (shouldThrowOnGraphqlError && typeof data === 'object' && data !== null && 'errors' in data) {
    const graphqlErrorData = graphqlErrorDataSchema.safeParse(data);

    if (graphqlErrorData.success) {
      logger.error('Graphql error data received querying udacity service.', {
        file: 'src/utils/query-udacity-service.ts',
        function: 'queryUdacityService',
        statusCode: response.status,
        dataReceived: JSON.stringify(data),
        error: JSON.stringify(graphqlErrorData.data),
        url: url,
        body: body,
      });

      throw new QueryUdacityServiceGraphqlError('Error querying graphql service.', {
        dataReceived: data,
        statusCode: response.status,
        cause: {
          statusCode: response.status,
        },
      });
    }
  }

  /**
   * There are useful for logging mock data
   * Use program nd000 and project Animal Trading Cards
   */

  // if (url.includes('/contents')) {
  //   console.log(JSON.stringify(data));
  // }

  // if (body?.includes('FetchEnrollments')) {
  //   console.log(JSON.stringify(data));
  // }

  // If we're in production and the schema is provided, attempt to parse the data. If it fails, fall back to data that should pass the schema validation.
  if (zodSchema && publicEnv.NEXT_PUBLIC_ENVIRONMENT_NAME === 'production') {
    let zodFallbackData: unknown;

    try {
      return zodSchema.parse(data) as z.infer<T>;
    } catch (error) {
      logger.fatal('Error parsing response from udacity service.', {
        file: 'src/utils/query-udacity-service.ts',
        function: 'queryUdacityService',
        statusCode: response.status,
        dataReceived: JSON.stringify(data),
        error: JSON.stringify(error),
        url: url,
        body: body,
      });

      try {
        zodFallbackData = generateZodFallback({ schema: zodSchema, data });
        return zodSchema.parse(zodFallbackData) as z.infer<T>;
      } catch (error) {
        logger.fatal('Error parsing response from zod fallback.', {
          file: 'src/utils/query-udacity-service.ts',
          function: 'queryUdacityService',
          statusCode: response.status,
          dataReceived: JSON.stringify(data),
          fallbackData: JSON.stringify(zodFallbackData),
          error: JSON.stringify(error),
          url: url,
          body: body,
        });

        throw new QueryUdacitySchemaParsingError('Error parsing response from udacity service.', {
          dataReceived: data,
          statusCode: response.status,
          cause: {
            statusCode: response.status,
          },
        });
      }
    }
  }

  try {
    return zodSchema ? (zodSchema.parse(data) as z.infer<T>) : (data as z.infer<T>);
  } catch (error) {
    logger.fatal('Error parsing response from udacity service.', {
      file: 'src/utils/query-udacity-service.ts',
      function: 'queryUdacityService',
      statusCode: response.status,
      dataReceived: JSON.stringify(data),
      error: JSON.stringify(error),
      url: url,
      body: body,
    });

    throw new QueryUdacitySchemaParsingError('Error parsing response from udacity service.', {
      dataReceived: data,
      statusCode: response.status,
      cause: {
        statusCode: response.status,
      },
    });
  }
}
